import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';

import { combineReducers } from 'redux';

import { BehaviorSubject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { combineEpics } from 'redux-observable';

import { mergeStrategyCombine } from 'utils/mergeStrategy';

/**
 * DynamicReducer
 * ==============
 *
 * Reducers are used to describe changes to the store state in response to particular actions.
 *
 * This is a smarter-than-average reducer that allows you to register additional namespaces and
 * entity types dynamically. This is the kind of thing you'll want to do if you're dynamically
 * loading parts of an application or trying to separate sub-application logic within a larger app.
 */
export default class DynamicReducer {
    /** @var {Object} */
    reducers = {};
    /** @var {Object} */
    defaultEntities = {};
    /** @var {Object[]} */
    entitiesReducers = [];

    constructor(defaultMergeStrategy) {
        this.setDefaultMergeStrategy(defaultMergeStrategy || mergeStrategyCombine);
    }

    setDefaultMergeStrategy(defaultMergeStrategy) {
        this.defaultMergeStrategy = defaultMergeStrategy;
    }

    createEpicSubject(epics = []) {
        // TODO: Implement ~epics-state to not repeat epics
        const epic$ = new BehaviorSubject(combineEpics(...epics));

        this.epic$ = epic$;

        const rootEpic = (action$, state$) => epic$.pipe(mergeMap((epic) => epic(action$, state$)));

        return rootEpic;
    }

    registerEpics(epics) {
        this.epic$.next(epics);
    }

    registerNamespace(name, reducer, override) {
        if (!name) {
            throw new Error('DynamicReducer Error: Empty value supplied for namespace!');
        }
        if (name in this.reducers && !override) {
            throw new Error(`DynamicReducer Error: "${name}" is already a registered namespace!`);
        }
        this.reducers[name] = reducer;
    }

    registerEntityReducer(reducer, entities, defaultEntities) {
        this.entitiesReducers = [...this.entitiesReducers, { reducer, entities }];
        this.defaultEntities = {
            ...this.defaultEntities,
            ...defaultEntities,
        };
    }

    entitiesReducer() {
        return (state, action) => {
            // compose entities state with previous state and the default entities.
            const nextState = { ...this.defaultEntities, ...state };

            const meta = {};
            let restEntities = [];

            this.entitiesReducers.forEach(({ reducer, entities }) => {
                // Entity reducers using combineReducers MUST provde entity list for filtering!
                // Redux gets unhappy when it encounters keys it doesn't expect, and Registered
                // entity reducers are unaware of each other. Thus, we need to filter the
                // shared entity state down to the keys that the individual reducer expects.
                let reducerState = nextState;
                if (!isEmpty(entities)) {
                    reducerState = pick(reducerState, entities);
                    restEntities = restEntities.concat(entities);
                }
                let reducerNextState = reducer(reducerState, action, { ...state }, meta);
                // Filter again afterwards to prevent the reducer from adding extra entities
                if (!isEmpty(entities)) {
                    reducerNextState = pick(reducerNextState, entities);
                }
                Object.assign(nextState, reducerNextState);
            });

            if (action.payload && action.payload.entities) {
                return this.defaultMergeStrategy(nextState, omit(action.payload.entities, restEntities));
            }

            return nextState;
        };
    }

    createReducer() {
        return combineReducers({
            ...this.reducers,
            entities: this.entitiesReducer(),
        });
    }
}
